import { MailOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { Modal, Spin, Steps, Typography } from 'antd';
import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { Button } from '@/components/molecules/Button';
import { FormField } from '@/components/molecules/FormField';
import { FileUploadModel } from '@/models';
import { HRCreateStep1 } from '@/modules/hr-management/components/form/HRCreateStep1';
import { HRCreateStep2 } from '@/modules/hr-management/components/form/HRCreateStep2';
import { HRCreateStep3 } from '@/modules/hr-management/components/form/HRCreateStep3';
import { useCreateHR } from '@/modules/hr-management/hooks/useCreateHR';
import { FormHRModel, HRGender } from '@/modules/hr-management/models';
import { getHRForm1Schema } from '@/modules/hr-management/services/validation';
import { history } from '@/utils';

const defaultValues: FormHRModel = {
  avatar: '',
  birthday: '',
  email: '',
  gender: HRGender.male,
  name: '',
  phone: '',
};

const CreateHR: React.FC = () => {
  const [current, setCurrent] = useState(0);
  // Mật khẩu KHÔNG được hiển thị trên giao diện (rủi ro lộ thông tin đăng
  // nhập + admin không kịp ghi lại). Backend tự gửi email thông tin đăng nhập
  // cho nhân sự; ở đây chỉ xác nhận email đã được gửi tới địa chỉ nào.
  const [createdInfo, setCreatedInfo] = useState<{
    name?: string;
    email?: string;
    // Chỉ có giá trị khi hệ thống KHÔNG gửi được email (SMTP sai cấu hình) —
    // khi đó phải hiện mật khẩu để admin còn đọc cho nhân sự.
    fallbackPassword?: string;
  } | null>(null);

  const next = () => {
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const resolver = yupResolver(getHRForm1Schema());
  const formMethods = useForm<FormHRModel>({
    defaultValues,
    resolver: current === 0 ? resolver : undefined,
  });
  const { mutateAsync: createHR, isLoading } = useCreateHR();
  const { mutateAsync } = useMutation(createHR);
  const onSubmit: SubmitHandler<FormHRModel> = async (formData) => {
    if (current < 1) {
      next();

      return;
    }
    const avatar = formData.avatar as FileUploadModel;
    const response = await mutateAsync({ ...formData, avatar: avatar.url });
    if (response) {
      setCreatedInfo({
        email: response.email || formData.email,
        fallbackPassword: response.generated_password,
        name: response.name || formData.name,
      });
      next();
    }
  };

  const steps = [
    {
      content: <HRCreateStep1 formMethods={formMethods} />,
      title: <h6>Thông tin cơ bản</h6>,
    },
    {
      content: <HRCreateStep2 formMethods={formMethods} />,
      title: <h6 className="text-nowrap">Phân quyền chức năng</h6>,
    },
    {
      content: <HRCreateStep3 formMethods={formMethods} />,
      title: <h6>Hoàn thành</h6>,
    },
  ];

  const items = steps.map((item) => ({ key: item.title, title: item.title }));

  return (
    <Spin spinning={isLoading}>
      {/* Xác nhận đã gửi thông tin đăng nhập qua email — không hiển thị mật
          khẩu trên giao diện. */}
      <Modal
        closable={false}
        footer={
          <Button className="rounded-pill" onClick={() => setCreatedInfo(null)} variant="primary">
            Đã hiểu
          </Button>
        }
        maskClosable={false}
        open={!!createdInfo}
        title={
          createdInfo?.fallbackPassword ? (
            <span>⚠️ Không gửi được email thông tin đăng nhập</span>
          ) : (
            <span>
              <MailOutlined rev="" /> Đã gửi thông tin đăng nhập qua email
            </span>
          )
        }
      >
        {createdInfo?.fallbackPassword ? (
          <>
            <Typography.Paragraph>
              Tài khoản của <strong>{createdInfo.name}</strong> đã được tạo, nhưng hệ thống{' '}
              <strong>không gửi được email</strong> tới <strong>{createdInfo.email}</strong>. Vui
              lòng kiểm tra lại cấu hình email tại menu <strong>Cấu hình tham số</strong> &rarr;
              &quot;Cấu hình Email (SMTP)&quot;.
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary">
              Trong lúc chờ, hãy đọc mật khẩu dưới đây cho nhân sự qua điện thoại/Zalo. Mật khẩu chỉ
              hiển thị một lần.
            </Typography.Paragraph>
            <Typography.Paragraph
              code
              copyable={{ tooltips: ['Copy', 'Đã copy'] }}
              style={{ fontSize: 20, padding: 12, textAlign: 'center' }}
            >
              {createdInfo.fallbackPassword}
            </Typography.Paragraph>
          </>
        ) : (
          <>
            <Typography.Paragraph>
              Tài khoản của <strong>{createdInfo?.name}</strong> đã được tạo thành công. Hệ thống đã
              gửi tên đăng nhập và mật khẩu tới email <strong>{createdInfo?.email}</strong>.
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary">
              Vì lý do bảo mật, mật khẩu không được hiển thị trên màn hình. Nếu nhân sự không nhận
              được email, vào chi tiết nhân sự và bấm <strong>&quot;Gửi lại&quot;</strong> ở mục
              &quot;Gửi lại mật khẩu vào email&quot;.
            </Typography.Paragraph>
          </>
        )}
        <Typography.Paragraph type="warning">
          Lần đầu đăng nhập, nhân sự phải đổi sang mật khẩu mới (≥ 8 ký tự).
        </Typography.Paragraph>
      </Modal>

      <FormField className="form" methods={formMethods} onSubmit={onSubmit}>
        <div className="card">
          <div className="card-body">
            <div className="card-header">
              <Steps current={current} items={items} labelPlacement="vertical" />
            </div>
            <div>{steps[current].content}</div>
            <div className="w-100 d-flex justify-content-end" style={{ marginTop: 24 }}>
              {current > 0 && current < 2 && (
                <Button
                  className="rounded-pill px-10 py-1 me-5"
                  onClick={() => prev()}
                  variant="outline"
                >
                  Quay lại
                </Button>
              )}
              {current === 0 && (
                <Button
                  className="rounded-pill px-10 py-1 me-5"
                  onClick={() => history.push('management/list')}
                  variant="outline"
                >
                  Thoát
                </Button>
              )}
              {current === steps.length - 1 && (
                <Button
                  className="rounded-pill px-10 py-1 me-5"
                  onClick={() => history.push('/hr-management/management/list')}
                  variant="outline"
                >
                  Đóng
                </Button>
              )}
              {current < steps.length - 1 && (
                <Button className="rounded-pill py-1 px-10" type="submit">
                  {current == 0 ? 'Tiếp tục' : 'Hoàn thành'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </FormField>
    </Spin>
  );
};

export default CreateHR;
